// Client-side pricing helpers - mirrors backend logic

export const USER_TYPES = [
  { value: 'end_user', label: 'End user', short: 'Personal', description: 'For personal DIY projects' },
  { value: 'builder', label: 'Builder / Tradesperson', short: 'Builder', description: 'For construction professionals' },
  { value: 'retailer', label: 'Retailer / Reseller', short: 'Retail', description: 'For businesses reselling products' },
];

export const getUserTypeLabel = (t) => USER_TYPES.find(u => u.value === t)?.label || 'End user';

/**
 * Compute per-unit price after bulk qty and discount.
 * All prices are EX-VAT. UI adds VAT display separately.
 *
 * @param {object} product
 * @param {number} qty
 * @param {string} userType end_user|builder|retailer
 * @param {number|object|null} override When object:
 *   { discount_pct?, fixed_price?, bulk_qty?, bulk_price? }.
 *   - `bulk_qty` + `bulk_price` (product-level only) replace the product's
 *     own bulk qty/price when the cart qty crosses the customer-specific
 *     threshold.
 *   - `fixed_price` (if set) forces the unit price and skips bulk & tier logic.
 *   - Otherwise `discount_pct` overrides the product's tier discount.
 *   Passing a bare number is treated as `discount_pct` for backward compat.
 * @param {object} [opts]
 * @param {boolean} [opts.collect] When true and the product has a
 *   `collection_price_<tier>` set, that price wins outright (no other stacking).
 */
export const computePricing = (product, qty = 1, userType = 'end_user', override = null, opts = {}) => {
  if (!product) return { unit: 0, base: 0, bulkApplied: false, discount: 0, saved: 0, overridden: false };

  const { collect = false } = opts || {};

  // 0. "I will collect" shortcut — collection price for this tier wins outright
  //    (no CRM/bulk/tier logic stacks on top). Only when the admin has actually
  //    set a collection price for this tier; otherwise fall through so the
  //    toggle is invisible and pricing is unchanged.
  if (collect) {
    const raw = product[`collection_price_${userType}`];
    const cp = raw != null && raw !== '' ? Number(raw) : 0;
    if (cp > 0) {
      const unit = +cp.toFixed(2);
      const saved = +(((product.price || 0) - unit) * qty).toFixed(2);
      return { unit, base: unit, bulkApplied: false, discount: 0, saved, overridden: false, fixedPriceApplied: false, collectApplied: true };
    }
  }

  let overrideObj = null;
  if (typeof override === 'number') overrideObj = { discount_pct: override };
  else if (override && typeof override === 'object') overrideObj = {
    discount_pct: override.discount_pct != null ? Number(override.discount_pct) : null,
    fixed_price: override.fixed_price != null ? Number(override.fixed_price) : null,
    bulk_qty: override.bulk_qty != null ? Number(override.bulk_qty) : null,
    bulk_price: override.bulk_price != null ? Number(override.bulk_price) : null,
  };

  // 1. Customer bulk contract wins outright at bulk qty.
  if (overrideObj && overrideObj.bulk_qty && overrideObj.bulk_price != null && qty >= overrideObj.bulk_qty) {
    const unit = +Number(overrideObj.bulk_price).toFixed(2);
    const saved = +(((product.price || 0) - unit) * qty).toFixed(2);
    return { unit, base: unit, bulkApplied: true, discount: 0, saved, overridden: true, fixedPriceApplied: false, customBulkApplied: true };
  }

  // 2. Fixed unit price contract.
  if (overrideObj && overrideObj.fixed_price != null && !Number.isNaN(overrideObj.fixed_price)) {
    const unit = +Number(overrideObj.fixed_price).toFixed(2);
    const saved = +(((product.price || 0) - unit) * qty).toFixed(2);
    return { unit, base: +(product.price || 0).toFixed(2), bulkApplied: false, discount: 0, saved, overridden: true, fixedPriceApplied: true };
  }

  // 3. Pick base (standard OR product bulk if triggered).
  //    Trade rule: the standard bulk offer is a BUILDER concession only.
  //    End users and retailers do NOT get the standard bulk price without
  //    a customer-specific CRM override. The UI hides the bulk band too.
  let base = product.price || 0;
  let bulkApplied = false;
  const hideStdBulkForNonBuilder = userType !== 'builder' && !overrideObj;
  if (!hideStdBulkForNonBuilder && product.bulk_qty && product.bulk_price != null && qty >= product.bulk_qty) {
    base = product.bulk_price;
    bulkApplied = true;
  }

  // 4. Customer % override on the chosen base.
  if (overrideObj && overrideObj.discount_pct != null && overrideObj.discount_pct > 0) {
    const unit = +(base * (1 - Number(overrideObj.discount_pct) / 100)).toFixed(2);
    const saved = +(((product.price || 0) - unit) * qty).toFixed(2);
    return { unit, base: +base.toFixed(2), bulkApplied, discount: Number(overrideObj.discount_pct), saved, overridden: true, fixedPriceApplied: false };
  }

  // 5. Manual fixed price per tier (Advanced Group Pricing).
  //    Overrides the tier's % discount when set (>0). Skipped when a product
  //    bulk qty is triggered (bulk always wins on its own — this matches how
  //    tier % discounts behave).
  if (!bulkApplied) {
    const rawMp = product[`manual_price_${userType}`];
    const mp = rawMp != null && rawMp !== '' ? Number(rawMp) : 0;
    if (mp > 0) {
      const unit = +mp.toFixed(2);
      const saved = +(((product.price || 0) - unit) * qty).toFixed(2);
      return { unit, base: +(product.price || 0).toFixed(2), bulkApplied: false, discount: 0, saved, overridden: false, fixedPriceApplied: false, manualTierApplied: true };
    }
  }

  // 6. Tier discount — applied ONLY to the standard price.
  //    If the product's own bulk qty is triggered, the tier discount is NOT applied
  //    on top; only a CRM override could reduce the bulk price further.
  let discount = 0;
  if (!bulkApplied) {
    discount = Number(product[`discount_${userType}`] || 0);
    if (discount > 0) base = base * (1 - discount / 100);
  }
  let unit = +base.toFixed(2);

  // 7. Builder concession: automatically show the LOWEST of
  //    (a) the tier-discounted standard price, (b) the product's bulk price,
  //    (c) any CRM override (already applied above).
  //    This runs even when the cart qty is below the product's bulk threshold —
  //    the intent is "trade customers should never overpay by missing an offer".
  if (userType === 'builder' && !overrideObj) {
    const candidates = [unit];
    if (product.bulk_price != null && !Number.isNaN(Number(product.bulk_price))) {
      candidates.push(Number(product.bulk_price));
    }
    unit = +Math.min(...candidates).toFixed(2);
    if (unit < base) bulkApplied = true;   // signal that a non-standard price is applied
  }

  const saved = +(((product.price || 0) - unit) * qty).toFixed(2);
  return { unit, base: +base.toFixed(2), bulkApplied, discount, saved, overridden: false, fixedPriceApplied: false };
};

/**
 * VAT helpers. All catalog prices are excluding VAT.
 * @param {number} amountExVat
 * @param {number} vatRate percentage e.g. 20
 */
export const addVat = (amountExVat, vatRate = 20) =>
  +((amountExVat || 0) * (1 + (vatRate || 0) / 100)).toFixed(2);

export const vatAmount = (amountExVat, vatRate = 20) =>
  +((amountExVat || 0) * ((vatRate || 0) / 100)).toFixed(2);

/**
 * Per-product shipping override calculator.
 * Priority per cart line:
 *   1. If line has `collect === true` → ships free (customer is collecting).
 *   2. If product has `free_delivery_qty > 0` AND qty >= free_delivery_qty → this line ships FREE.
 *   3. Else if product has `individual_delivery_cost > 0` → line delivery = individual_delivery_cost * qty.
 *   4. Else the line falls back into the global free-delivery threshold pool.
 * Global fallback: FREE if the pooled subtotal is 0 or >= threshold, otherwise `flatRate`.
 *
 * Returns { total, overrideDelivery, fallbackDelivery, fallbackSubtotal, collectSubtotal }.
 * All fields are numbers (rounded to 2 dp for money).
 */
/**
 * Find the matching pallet-slab cost for `qty` from a product's slab table.
 * Slab row shape: { min_qty, max_qty, cost }. `max_qty === 0` means no upper bound.
 * Returns null when the product has no slabs or no slab matches — caller should
 * then fall back to the existing per-product / global shipping logic.
 */
export const matchShippingSlab = (slabs = [], qty = 0) => {
  if (!Array.isArray(slabs) || slabs.length === 0 || qty <= 0) return null;
  for (const row of slabs) {
    const lo = Number(row?.min_qty || 0);
    const hi = Number(row?.max_qty || 0);
    if (lo <= qty && (hi === 0 || qty <= hi)) {
      return Number(row?.cost || 0);
    }
  }
  return null;
};

export const computeDelivery = (items = [], { threshold = 75, flatRate = 4.95 } = {}) => {
  let overrideDelivery = 0;
  let fallbackSubtotal = 0;
  let collectSubtotal = 0;
  for (const it of items) {
    const qty = Number(it.qty || 0);
    const price = Number(it.price || 0);
    const line = price * qty;
    if (it.collect === true) {
      // Customer is collecting this line — no shipping applies and it does
      // NOT count toward the free-delivery threshold pool.
      collectSubtotal += line;
      continue;
    }
    // Tiered pallet-slab wins over legacy per-product rules when defined.
    const slabCost = matchShippingSlab(it.shipping_slabs, qty);
    if (slabCost != null) {
      overrideDelivery += slabCost;
      continue;
    }
    const freeQty = Number(it.free_delivery_qty || 0);
    const perUnit = Number(it.individual_delivery_cost || 0);
    const hasOverride = freeQty > 0 || perUnit > 0;
    if (!hasOverride) {
      fallbackSubtotal += line;
      continue;
    }
    if (freeQty > 0 && qty >= freeQty) {
      // free line
      continue;
    }
    if (perUnit > 0) {
      overrideDelivery += perUnit * qty;
    }
    // else free_qty set but not met and no per-unit cost → line ships free
  }
  const fallbackDelivery = fallbackSubtotal === 0 || fallbackSubtotal >= Number(threshold) ? 0 : Number(flatRate);
  return {
    total: +(overrideDelivery + fallbackDelivery).toFixed(2),
    overrideDelivery: +overrideDelivery.toFixed(2),
    fallbackDelivery,
    fallbackSubtotal: +fallbackSubtotal.toFixed(2),
    collectSubtotal: +collectSubtotal.toFixed(2),
  };
};
